import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import CguUserHistoryDialog from "@/components/admin/CguUserHistoryDialog";
import FrDateField from "@/components/FrDateField";

const input = "rounded-xl bg-white px-3 py-2 text-[13px] font-bold street-border-thin";

// Suivi des acceptations / refus des CGU.
export default function CguAcceptancesPanel({ versions }) {
  const [f, setF] = useState({ version_id: "", q: "", status: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [historyOf, setHistoryOf] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/admin/cgu/acceptances", { params }).then(({ data: d }) => setData(d)).catch(() => setData({ items: [], stats: {}, refusals: [] }));
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const s = data?.stats || {};
  const notAccepted = data?.mode === "not_accepted";

  return (
    <div className="space-y-3" data-testid="cgu-acceptances-panel">
      <h3 className="font-[Outfit] text-[18px] font-black">Suivi des acceptations</h3>
      <div className="grid gap-2 sm:grid-cols-4">
        <Stat label="Comptes passager / conducteur" value={s.users_total} testid="cgu-stat-total" />
        <Stat label="Ont accepté la dernière version" value={s.up_to_date} testid="cgu-stat-up-to-date" />
        <Stat label="N'ont pas encore accepté" value={s.pending} testid="cgu-stat-pending" />
        <Stat label="Taux d'acceptation" value={`${s.percent ?? 0} %`} testid="cgu-stat-percent" />
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={f.status} onChange={set("status")} className={input} data-testid="cgu-filter-status">
          <option value="">Toutes les acceptations</option>
          <option value="accepted">CGU acceptées (dernière version)</option>
          <option value="not_accepted">CGU non acceptées</option>
        </select>
        {!notAccepted && (
          <>
            <select value={f.version_id} onChange={set("version_id")} className={input} data-testid="cgu-acceptances-version">
              <option value="">Toutes les versions</option>
              {versions.map((v) => <option key={v.id} value={v.id}>Version {v.version}</option>)}
            </select>
            <div className="w-36"><FrDateField value={f.date_from} onChange={(v) => setF((x) => ({ ...x, date_from: v }))} placeholder="Du" testid="cgu-filter-date-from" /></div>
            <div className="w-36"><FrDateField value={f.date_to} onChange={(v) => setF((x) => ({ ...x, date_to: v }))} placeholder="Au" testid="cgu-filter-date-to" /></div>
          </>
        )}
        <div className="flex items-center gap-1.5 rounded-xl bg-white px-3 street-border-thin">
          <Search className="h-4 w-4 text-foreground/50" />
          <input value={f.q} onChange={set("q")} placeholder="Rechercher un e-mail" className="bg-transparent py-2 text-[13px] font-semibold outline-none" data-testid="cgu-acceptances-search" />
        </div>
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin" /> : notAccepted
        ? <NotAcceptedList users={data.users || []} onOpen={setHistoryOf} />
        : <AcceptedTable items={data.items} onOpen={setHistoryOf} />}
      {data?.refusals?.length > 0 && (
        <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid="cgu-refusals">
          <div className="text-[13px] font-black">Refus récents</div>
          {data.refusals.slice(0, 10).map((r) => (
            <div key={r.id} className="text-[12px] font-semibold text-foreground/70">{r.email || "—"} · v{r.version} · {new Date(r.at).toLocaleString("fr-FR")}</div>
          ))}
        </div>
      )}
      {historyOf && <CguUserHistoryDialog userId={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}

function AcceptedTable({ items, onOpen }) {
  if (!items.length) return <p className="rounded-2xl bg-white p-3 text-[13px] font-semibold street-border-thin" data-testid="cgu-acceptances-empty">Aucune acceptation pour ces filtres.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl bg-white street-border-thin">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-[#FFE9D6] font-black"><tr><th className="p-2">Utilisateur</th><th className="p-2">Version</th><th className="p-2">Date et heure</th><th className="p-2">Contexte</th></tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="cursor-pointer border-t border-foreground/10 hover:bg-[#FFF8F1]" onClick={() => onOpen(a.user_id)} data-testid={`cgu-acceptance-${a.id}`}>
              <td className="max-w-[160px] break-all p-2 font-bold">{a.email || "Compte supprimé"}<div className="text-[11px] font-semibold text-foreground/50">{a.role === "driver" ? "Conducteur" : "Passager"}</div></td>
              <td className="p-2">{a.version}</td>
              <td className="p-2">{new Date(a.accepted_at).toLocaleString("fr-FR")}<div className="text-[11px] text-foreground/50">IP {a.ip || "—"}</div></td>
              <td className="p-2">{a.method === "signup" ? "Inscription" : "Mise à jour"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NotAcceptedList({ users, onOpen }) {
  if (!users.length) return <p className="rounded-2xl bg-white p-3 text-[13px] font-semibold street-border-thin" data-testid="cgu-not-accepted-empty">Tous les comptes ont accepté la dernière version.</p>;
  return (
    <div className="space-y-1.5" data-testid="cgu-not-accepted-list">
      {users.map((u) => (
        <button key={u.id} type="button" onClick={() => onOpen(u.id)} className="flex w-full items-center justify-between rounded-xl bg-white px-3 py-2 text-left text-[12px] font-bold street-border-thin" data-testid={`cgu-not-accepted-${u.id}`}>
          <span className="min-w-0 flex-1 truncate">{u.full_name ? <>{u.full_name}<span className="ml-2 font-semibold text-foreground/50">{u.email}</span></> : u.email}</span>
          <span className="ml-2 shrink-0 text-foreground/60">{u.role === "driver" ? "Conducteur" : "Passager"}</span>
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid={testid}>
      <div className="font-[Outfit] text-[24px] font-black">{value ?? 0}</div>
      <div className="text-[12px] font-bold text-foreground/60">{label}</div>
    </div>
  );
}
